import {
  createContext,
  useCallback,
  useContext,
  useState,
  ComponentType,
  useRef,
  MutableRefObject,
} from 'react';
import { isAiMode } from 'utils';
import { getHost } from 'utils/tokenService';
import { Page } from '@thoughtspot/visual-embed-sdk';
import { usePasteJSONModal } from 'hooks/usePasteJSONModal';
import { viewType, MessageType, MessageRoleType } from 'types';
import { useDownloadJSONModal } from 'hooks/useDownloadJSONModal';

export const AppContext = createContext({
  isHeaderLoading: false,
  setIsHeaderLoading: (value: boolean) => {
    value;
  },
  embedSelections: {
    pinboard: '',
    viz: '',
    dataSource: '',
    answerId: '',
    pageId: Page.Home,
    searchType: 'datasource',
  },
  onEmbedSelectionChange: (selections) => {
    selections;
  },
  goBack: () => {
    void 0;
  },
  setEmbedSelections: (selections) => {
    selections;
  },
  toastMessageProps: {
    message: '',
    isVisible: false,
  },
  setMessageHandler: (message: string) => {
    message;
  },
  openDownloadModal: () => {
    void 0;
  },
  openPasteModal: () => {
    void 0;
  },
  DownloadModal: (() => null) as ComponentType<{ setMessageHandler: (message: string) => void }>,
  PasteModal: (() => null) as ComponentType<{ setMessageHandler: (message: string) => void }>,
  activeView: 'liveboard' as viewType,
  setActiveView: (view: viewType) => {
    view;
  },
  isAiModeActive: false,
  setIsAiModeActive: (value: boolean) => {
    value;
  },
  popUpMessageType: 'string',
  setPopUpMessageType: (type) => {
    type;
  },
  isStreaming: false,
  setIsStreaming: (value: boolean) => {
    value;
  },
  chatMessages: [] as MessageType[],
  updateChatMessages: (messages: MessageType[]) => {
    messages;
  },
  updateLastAiMessage: (message: string, id: MessageRoleType) => {
    message;
    id;
  },
  updateLastTwoMessages: (message: string) => {
    message;
  },
  resetChatMessages: () => {
    void 0;
  },
  abortControllerRef: { current: null } as MutableRefObject<AbortController | null>,
});

export const AppContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [embedSelections, setEmbedSelections] = useState({
    pinboard: '',
    viz: '',
    dataSource: '',
    answerId: '',
    pageId: Page.Home,
    searchType: 'datasource',
  });
  const onEmbedSelectionChange = (selections) => {
    setEmbedSelections((prev) => ({
      ...prev,
      ...selections,
    }));
  };

  const [loadings, setLoadings] = useState({
    header: false,
    footer: false,
  });
  const setIsHeaderLoading = (value: boolean) => {
    setLoadings((prev) => ({ ...prev, header: value }));
  };
  const goBack = () => {
    window.parent.postMessage(
      {
        type: 'GO_BACK',
      },
      getHost()
    );
  };

  const [toastMessageProps, setToastMessageProps] = useState({
    message: '',
    isVisible: false,
  });
  const setMessageHandler = useCallback((message: string) => {
    setToastMessageProps({
      message: message,
      isVisible: true,
    });
    setTimeout(() => {
      setToastMessageProps({
        message: '',
        isVisible: false,
      });
    }, 5000);
  }, []);

  const { DownloadModal, openDownloadModal } = useDownloadJSONModal();
  const { PasteModal, openPasteModal } = usePasteJSONModal();

  const [activeView, setActiveView] = useState<viewType>('liveboard');
  const [isAiModeActive, setIsAiModeActive] = useState(isAiMode());

  const [popUpMessageType, setPopUpMessageType] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);

  const [chatMessages, setChatMessages] = useState<MessageType[]>([]);
  const updateChatMessages = useCallback((messages: MessageType[]) => {
    setChatMessages((prev) => [...prev, ...messages]);
  }, []);
  const updateLastAiMessage = (
    message: string,
    id: MessageRoleType,
    isManualModeActive?: boolean
  ) => {
    setChatMessages((prev) => {
      const updated = [...prev];
      updated[updated.length - 1] = {
        id: updated[updated.length - 1].id.replace(MessageRoleType.AI, id),
        role: updated[updated.length - 1].role,
        content: message,
        isManualModeActive,
      };
      return updated;
    });
  };

  const updateLastTwoMessages = (message: string) => {
    setChatMessages((prev) => {
      const updated = [...prev];
      updated[updated.length - 1] = {
        ...updated[updated.length - 1],
        id: updated[updated.length - 1].id.replace(MessageRoleType.ISSUE, MessageRoleType.AI),
        content: '',
      };
      updated[updated.length - 2] = {
        ...updated[updated.length - 2],
        content: message,
      };
      return updated;
    });
  };
  const resetChatMessages = () => {
    setChatMessages([]);
  };

  const abortControllerRef = useRef<AbortController | null>(null);

  return (
    <AppContext.Provider
      value={{
        isHeaderLoading: loadings.header,
        setIsHeaderLoading,
        embedSelections,
        onEmbedSelectionChange,
        goBack,
        setEmbedSelections,
        toastMessageProps,
        setMessageHandler,
        openDownloadModal,
        openPasteModal,
        DownloadModal,
        PasteModal,
        activeView,
        setActiveView,
        isAiModeActive,
        setIsAiModeActive,
        popUpMessageType,
        setPopUpMessageType,
        isStreaming,
        setIsStreaming,
        chatMessages,
        updateChatMessages,
        updateLastAiMessage,
        updateLastTwoMessages,
        resetChatMessages,
        abortControllerRef,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  return useContext(AppContext);
};
